<template>
  <div>
    <v-dialog v-model="proxy" scrollable fullscreen transition="dialog-bottom-transition" content-class="org-sheet" :retain-focus="false" @input="onDialogToggle">
      <v-card class="rounded-0">
        <v-card-title class="org-head pa-0">
          <div class="org-head__inner">
            <!-- 標題列：三欄格線，標題才會真的在正中間。左邊只放人數 -->
            <div class="org-head__bar">
              <div class="org-head__count">
                <!-- 載入中不要顯示 0 —— 那是還沒拿到資料，不是真的沒有下線 -->
                <v-progress-circular v-if="orgLoading" indeterminate size="18" width="2" color="primary" />
                <span v-else class="primary--text" :aria-label="`共 ${downlineLenght} 人`">{{ downlineLenght }}</span>
              </div>
              <div class="org-head__title">分享紀錄</div>
              <div class="org-head__close">
                <v-btn icon aria-label="關閉" @click="proxy = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- 搜尋＋列表／組織圖切換（只留圖示） -->
            <div class="org-head__tools">
              <v-text-field
                v-model="q"
                dense
                clearable
                hide-details
                outlined
                prepend-inner-icon="mdi-magnify"
                placeholder="搜尋姓名、信箱、位階"
                class="org-head__search"
                @input="onSearchInput"
              />
              <v-btn-toggle v-model="viewMode" dense mandatory color="primary" class="org-head__view">
                <v-btn value="list" height="40" aria-label="列表">
                  <v-icon>mdi-format-list-bulleted</v-icon>
                </v-btn>
                <v-btn value="tree" height="40" aria-label="組織圖">
                  <v-icon>mdi-sitemap</v-icon>
                </v-btn>
              </v-btn-toggle>
            </div>

            <!-- 搜尋時才有意義：比對到的人要不要連上層／下層一起顯示 -->
            <v-expand-transition>
              <div v-if="hasQuery" class="org-head__keep">
                <span class="org-head__keep-label">找到 {{ matchCount }} 人，一併顯示</span>
                <v-chip small filter outlined :input-value="keepAncestors" color="primary" @click="keepAncestors = !keepAncestors">上層</v-chip>
                <v-chip small filter outlined :input-value="includeDescendants" color="primary" @click="includeDescendants = !includeDescendants">下層</v-chip>
              </div>
            </v-expand-transition>
          </div>
        </v-card-title>
        <v-card-text class="text-body-1 text--primary org-panel pa-0">
          <!--
            骨架屏。

            從信件連結（/center?tab=agent&org=1）進來時，dialog 會在資料還沒
            回來之前就開啟 —— 原本那一瞬間是整片空白，下線愈多空白愈久，
            使用者的體感是「點了沒反應」。先給骨架，至少看得出正在載入。
          -->
          <div v-if="orgLoading" class="pa-4">
            <v-skeleton-loader v-for="i in 5" :key="i" type="list-item-avatar-two-line" class="mb-2" />
          </div>
          <!-- 組織圖：由左往右長，手機直著看也放得下（OrgChart.vue 開頭有說明） -->
          <div v-else-if="viewMode === 'tree'" class="org-chart-wrap">
            <OrgChart ref="org" :referrals="filteredReferrals" :root="{ name: $store.state.userRealName, grade: userGradeName }" :search-q="q" />
          </div>

          <div v-else class="org-list-wrap">
            <div style="max-width: 900px; width: 100%">
              <OrgList :referrals="filteredReferrals" :search-q="q" />
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Referral_GetDownlines, Grade_GetUserGradeName } from "@/apis/api.js";
import OrgChart from "../Referral/OrgChart.vue";
import OrgList from "../Referral/OrgList.vue";

export default {
  name: "ShareOrgDialog",
  props: {
    value: { type: Boolean, default: false }, // ← v-model 對應的 prop
  },
  components: {
    OrgChart,
    OrgList,
  },
  data() {
    return {
      qrcodeImg: require("@/assets/image/serivceIcon/qrcode_icon.svg"),
      qrDialog: false,
      referralList: [],
      /*
          組織資料還在路上。

          預設 true —— 這個元件沒有 v-if，掛載時就會發請求，而從信件連結
          （?org=1）進來時 dialog 幾乎同時開啟。預設 false 的話，開啟到第一次
          setState 之間會閃一下「共 0 人」與空白清單。
      */
      orgLoading: true,
      q: "", // 搜尋字串
      keepAncestors: true, // 是否保留上層（建議預設 true）
      includeDescendants: false, // 是否保留下層
      userGradeName: null,

      viewMode: "list", // 'tree' | 'list'
      openedGroups: [], // 列表模式：展開中的 group guid
      downlineLenght: 0,
    };
  },
  computed: {
    proxy: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    hasQuery() {
      return (this.q || "").toString().trim().length > 0;
    },
    /** 搜尋比對到幾個人（不含因為「上層／下層」一起留下來的） */
    matchCount() {
      return this.highlightIds.length;
    },
    // 命中節點 id（拿來高亮）
    highlightIds() {
      if (!this.q) return [];
      const { matches } = this.filterCompute(this.referralList, this.q);
      return Array.from(matches);
    },

    // 真正傳給組織圖、列表的過濾結果（含上/下層）
    filteredReferrals() {
      if (!this.q) return this.referralList;
      const { keepSet } = this.filterCompute(this.referralList, this.q, this.keepAncestors, this.includeDescendants);
      return this.referralList.filter((x) => keepSet.has(x.guid || x.Guid));
    },

    // 列表模式的樹狀資料
    listTreeData() {
      const list = this.filteredReferrals || [];
      return this._buildTreeByGuid(list);
    },
  },
  watch: {
    // 原本的資料與搜尋
    referrals: {
      handler() {
        this.renderChart?.();
      },
      deep: true,
    },
    root() {
      this.renderChart?.();
    },
    searchQ() {
      this.renderChart?.();
    },
    highlightIds() {
      this.renderChart?.();
    },

    // ADD: 搜尋字改變時，列表模式自動展開/收合
    q() {
      this._syncOpenedWithQuery();
    },

    // ADD: 切換視圖時，回到 Tree 要 reflow 一下
    viewMode(v) {
      if (v === "tree") {
        this.$nextTick(() => this.$refs.org && this.$refs.org.resize());
      } else {
        // 列表：依目前的 listTreeData 與 q 同步展開
        this._syncOpenedWithQuery();
      }
    },
  },

  mounted() {
    // 從 Store 或是 相容性 Key 取得推薦碼
    let emailCode = this.$store.state.username || localStorage.getItem("_emailEncrypt");
    if (emailCode && emailCode !== "null" && emailCode !== "undefined") {
      this.GetDownlines_api(emailCode);
      this.getUserGradeName_api(emailCode);
    } else {
      // 沒有推薦碼就不會發請求，loading 要在這裡收掉 ——
      // 否則骨架屏會一直轉，看起來像卡住
      this.orgLoading = false;
    }
  },
  methods: {
    onSearchInput() {
      this.$nextTick(() => {
        if (this.$refs.org) this.$refs.org.resize();
      });
    },
    onDialogToggle(val) {
      if (val) this.$nextTick(() => this.$refs.org?.resize());
    },
    getUserGradeName_api(emailCode) {
      if (!emailCode || emailCode === "null" || emailCode === "undefined") return;
      Grade_GetUserGradeName(emailCode)
        .then((res) => {
          this.userGradeName = res.data;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {});
    },

    async GetDownlines_api(emailCode) {
      // 沒有推薦碼就不會有請求 —— loading 要自己收掉，否則永遠停在骨架屏
      if (!emailCode || emailCode === "null" || emailCode === "undefined") {
        this.orgLoading = false;
        return;
      }
      this.orgLoading = true;
      try {
        const { data } = await Referral_GetDownlines(emailCode);
        // 後端可能回 { data: [...] } 或直接回 [...]
        //  ↓↓↓ 兩種都支援
        this.referralList = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        // console.log("downlines loaded obj:", this.referralList);
        this.downlineLenght = this.referralList.length;
        // this.referralList = [
        //   // 第一層（直屬）
        //   {
        //     guid: "942bcc55-7cff-4ccc-9157-08dd7bf8689a",
        //     email: "kingfongit@gmail.com",
        //     downlineName: "金小豐33",
        //     referralGrade: "創業中心代理商",
        //     parentGuid: "53cde44c-b37c-4615-bb57-5dcc1d6e160e",
        //     level: 0,
        //     createdDate: "2025/04/15",
        //   },
        //   {
        //     guid: "9dcb3056-9670-4611-7c5f-08dd8ecdbc09",
        //     email: "dark19930914@gmail.com",
        //     downlineName: "Abi Lin",
        //     referralGrade: "一級代理商",
        //     parentGuid: "53cde44c-b37c-4615-bb57-5dcc1d6e160e",
        //     level: 0,
        //     createdDate: "2025/05/09",
        //   },
        //   {
        //     guid: "9f75c803-2dc9-40f1-9993-08ddeb771f70",
        //     email: "alt.fp-2ooa66bf@yopmail.com",
        //     downlineName: "kftest001",
        //     referralGrade: "二級代理商",
        //     parentGuid: "53cde44c-b37c-4615-bb57-5dcc1d6e160e",
        //     level: 0,
        //     createdDate: "2025/09/04",
        //   },
        //   {
        //     guid: "9f75c803-2dc9-40f1-9993-08ddeb771f12",
        //     email: "alt.fp-2ooa66bf@yopmail.com",
        //     downlineName: "kftest001",
        //     referralGrade: "二級代理商",
        //     parentGuid: "53cde44c-b37c-4615-bb57-5dcc1d6e160e",
        //     level: 0,
        //     createdDate: "2025/09/04",
        //   },
        //   {
        //     guid: "9f75c803-2dc9-40f1-9993-08ddeb771f23",
        //     email: "alt.fp-2ooa66bf@yopmail.com",
        //     downlineName: "kftest001",
        //     referralGrade: "AI推廣中心",
        //     parentGuid: "53cde44c-b37c-4615-bb57-5dcc1d6e160e",
        //     level: 0,
        //     createdDate: "2025/09/04",
        //   },
        //   {
        //     guid: "9f75c803-2dc9-40f1-9993-08ddeb771f45",
        //     email: "alt.fp-2ooa66bf@yopmail.com",
        //     downlineName: "kftest001",
        //     referralGrade: "一級代理商",
        //     parentGuid: "53cde44c-b37c-4615-bb57-5dcc1d6e160e",
        //     level: 0,
        //     createdDate: "2025/09/04",
        //   },

        //   // 第二層（掛在 金小豐33）
        //   {
        //     guid: "c1111111-1111-4111-8111-111111111111",
        //     email: "child1+jin@example.com",
        //     downlineName: "小豐-1",
        //     referralGrade: "一級代理商",
        //     parentGuid: "942bcc55-7cff-4ccc-9157-08dd7bf8689a",
        //     level: 1,
        //     createdDate: "2025/09/10",
        //   },
        //   {
        //     guid: "c2222222-2222-4222-8222-222222222222",
        //     email: "child2+jin@example.com",
        //     downlineName: "小豐-2",
        //     referralGrade: "介紹人",
        //     parentGuid: "942bcc55-7cff-4ccc-9157-08dd7bf8689a",
        //     level: 1,
        //     createdDate: "2025/09/12",
        //   },

        //   // 第二層（掛在 Abi Lin）
        //   {
        //     guid: "b1111111-1111-4111-8111-aaaaaaaaaaaa",
        //     email: "child1+abi@example.com",
        //     downlineName: "Abi-1",
        //     referralGrade: "介紹人",
        //     parentGuid: "9dcb3056-9670-4611-7c5f-08dd8ecdbc09",
        //     level: 1,
        //     createdDate: "2025/09/18",
        //   },

        //   // 第二層（掛在 kftest001）
        //   {
        //     guid: "a1111111-1111-4111-8111-bbbbbbbbbbbb",
        //     email: "child1+kf@example.com",
        //     downlineName: "KF-1",
        //     referralGrade: "二級代理商",
        //     parentGuid: "9f75c803-2dc9-40f1-9993-08ddeb771f70",
        //     level: 1,
        //     createdDate: "2025/09/20",
        //   },
        //   {
        //     guid: "a2222222-2222-4222-8222-bbbbbbbbbbbb",
        //     email: "child2+kf@example.com",
        //     downlineName: "KF-2",
        //     referralGrade: "AI推廣中心",
        //     parentGuid: "9f75c803-2dc9-40f1-9993-08ddeb771f70",
        //     level: 1,
        //     createdDate: "2025/09/22",
        //   },

        //   // 第三層（掛在 小豐-1）
        //   {
        //     guid: "d1111111-1111-4111-8111-cccccccccccc",
        //     email: "g1+jin1@example.com",
        //     downlineName: "G-小豐1-1",
        //     referralGrade: "一級代理商",
        //     parentGuid: "c1111111-1111-4111-8111-111111111111",
        //     level: 2,
        //     createdDate: "2025/09/25",
        //   },
        //   {
        //     guid: "d2222222-2222-4222-8222-cccccccccccc",
        //     email: "g2+jin1@example.com",
        //     downlineName: "G-小豐1-2",
        //     referralGrade: "創業中心",
        //     parentGuid: "c1111111-1111-4111-8111-111111111111",
        //     level: 2,
        //     createdDate: "2025/09/26",
        //   },

        //   // 第三層（掛在 KF-1）
        //   {
        //     guid: "e1111111-1111-4111-8111-dddddddddddd",
        //     email: "g1+kf1@example.com",
        //     downlineName: "G-KF1-1",
        //     referralGrade: "AI推廣中心",
        //     parentGuid: "a1111111-1111-4111-8111-bbbbbbbbbbbb",
        //     level: 2,
        //     createdDate: "2025/09/28",
        //   },
        // ];

        // 資料到位後，讓圖表依新尺寸重算
        this.$nextTick(() => this.$refs.org?.resize());
      } catch (err) {
        console.error("GetDownlines failed", err);
      } finally {
        // 失敗也要收 —— 停在骨架屏會讓人以為還在載，比顯示空清單更難判斷
        this.orgLoading = false;
      }
    },
    // ---- 核心過濾：命中 + 保留上層/下層 ----
    filterCompute(list, rawQ, keepAncestors = true, includeDesc = false) {
      const q = String(rawQ || "")
        .trim()
        .toLowerCase();
      const byId = new Map();
      const parent = new Map();
      const children = new Map();

      const get = (x, A, a) => (x && (x[A] != null ? x[A] : x[a])) ?? null;

      list.forEach((x) => {
        const id = get(x, "Guid", "guid");
        const pid = get(x, "ParentGuid", "parentGuid");
        byId.set(id, x);
        parent.set(id, pid);
        if (pid) {
          if (!children.has(pid)) children.set(pid, []);
          children.get(pid).push(id);
        }
      });

      // 1) 找出命中節點
      const matches = new Set();
      for (const x of list) {
        const name = (get(x, "DownlineName", "downlineName") || "").toLowerCase();
        const email = (get(x, "Email", "email") || "").toLowerCase();
        const grade = (get(x, "ReferralGrade", "referralGrade") || "").toLowerCase();
        const date = (get(x, "CreatedDate", "createdDate") || "").toLowerCase();
        if ([name, email, grade, date].some((f) => f.includes(q))) {
          matches.add(get(x, "Guid", "guid"));
        }
      }

      // 2) 需要保留的 id：命中 + 上層 +（可選）下層
      const keepSet = new Set(matches);

      if (keepAncestors) {
        for (const id of matches) {
          let p = parent.get(id);
          while (p && !keepSet.has(p)) {
            keepSet.add(p);
            p = parent.get(p);
          }
        }
      }

      if (includeDesc) {
        const dfs = (id) => {
          const kids = children.get(id) || [];
          for (const k of kids) {
            if (!keepSet.has(k)) {
              keepSet.add(k);
              dfs(k);
            }
          }
        };
        for (const id of matches) dfs(id);
      }

      return { keepSet, matches };
    },

    //  平面 -> 樹（列表模式用）
    _normId(s) {
      return (s == null ? "" : String(s)).trim().toLowerCase();
    },
    _buildTreeByGuid(list) {
      if (!Array.isArray(list) || !list.length) return [];
      const cloned = list.map((x) => ({
        ...x,
        guid: this._normId(x.guid || x.Guid),
        parentGuid: this._normId(x.parentGuid || x.ParentGuid),
        children: [],
      }));
      const map = Object.create(null);
      for (const n of cloned) if (!map[n.guid]) map[n.guid] = n;
      const hasParent = new Set();
      for (const n of cloned) {
        if (n.parentGuid && map[n.parentGuid]) {
          map[n.parentGuid].children.push(n);
          hasParent.add(n.guid);
        }
      }
      const roots = [];
      for (const n of cloned) if (!hasParent.has(n.guid)) roots.push(n);
      return roots;
    },

    //  列表展開控制
    _collectGroupGuids(nodes) {
      const out = [];
      const stack = [...(nodes || [])];
      while (stack.length) {
        const n = stack.pop();
        const kids = (n && n.children) || [];
        if (kids.length) out.push(n.guid);
        for (const c of kids) stack.push(c);
      }
      return out;
    },
    _syncOpenedWithQuery() {
      const hasQ = (this.q || "").toString().trim().length > 0;
      this.openedGroups = hasQ ? this._collectGroupGuids(this.listTreeData) : [];
    },
    onToggleGroup({ guid, open }) {
      const i = this.openedGroups.indexOf(guid);
      if (open && i < 0) this.openedGroups.push(guid);
      else if (!open && i >= 0) this.openedGroups.splice(i, 1);
    },
  },
};
</script>

<style>
/* 讓全屏 dialog 從上方留一條縫下來 */
.org-sheet {
  --top-gap: calc(50px + env(safe-area-inset-top));
  justify-content: flex-start !important;
  align-items: stretch !important;
  padding-top: var(--top-gap) !important;

  /* 確保覆蓋整個視窗（有些情況下需要） */
  position: fixed;
  inset: 0;
}

/* ★ 自製灰色遮罩（一定會有） */
.org-sheet::before {
  content: "";
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  pointer-events: none; /* 不擋點擊（交給 v-dialog 自己處理） */
}

/* 面板尺寸與圓角 */
.org-sheet > .v-dialog.v-dialog--fullscreen {
  position: relative;
  z-index: 1; /* 蓋在自製遮罩上 */
  height: calc(100dvh - var(--top-gap)) !important;
  margin: 0 !important;
  max-width: 100% !important;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  overflow: hidden;
}

/* 讓你的卡片承接圓角（若有 rounded-0 會被覆蓋） */
.org-sheet .v-card {
  border-radius: 16px 16px 0 0 !important;
}
</style>

<style scoped>
.org-head {
  display: block;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.org-head__inner {
  max-width: 900px;
  margin: 0 auto;
  padding: 6px 12px 12px;
}

.org-head__bar {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  min-height: 48px;
}

.org-head__count {
  padding-left: 4px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.org-head__title {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.org-head__close {
  display: flex;
  justify-content: flex-end;
}

.org-head__tools {
  display: flex;
  align-items: center;
  gap: 8px;
}

.org-head__search {
  flex: 1 1 auto;
  min-width: 0;
}

.org-head__view {
  flex-shrink: 0;
}

.org-head__keep {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 10px;
}

.org-head__keep-label {
  font-size: 13px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  margin-right: 2px;
}

.org-panel {
  display: flex;
  flex-direction: column;
  max-height: calc(100dvh - 120px);
}
.org-toolbar {
  flex: 0 0 auto;
}
/* 組織圖自己捲動（OrgChart 裡面），外面不要再出一條捲軸 */
.org-chart-wrap {
  position: relative;
  flex: 1 1 auto;
  min-height: 320px;
  overflow: hidden;
}

/* 組織圖撐滿這一塊（百分比高度在 flex 裡不一定算得出來，用絕對定位最穩） */
.org-chart-wrap > .org-chart {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: auto;
}

.org-list-wrap {
  display: flex;
  justify-content: center;
  min-height: 320px;
  width: 100%;
  overflow: auto;
  background: #f7f7fb;
}
.switch-row {
  display: inline-flex;
  align-items: center;
  gap: 6px; /* 想多近改這裡 */
  white-space: nowrap; /* 文字不換行 */
}
</style>
